import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";
import SocialConnectForm from "@/components/wifi/SocialConnectForm";
import SocialConnectSuccess from "@/components/wifi/SocialConnectSuccess";

const API = `${API_URL}/api/v1`;

const CSS = `
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes fadeSlide{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulseRing{0%{transform:scale(1);opacity:.45}100%{transform:scale(2.6);opacity:0}}
  .sc-input::placeholder{color:#9CA3AF}
  .sc-input:focus{outline:none}
  .sc-fade{animation:fadeSlide .32s ease both}
`;

function resolveLogoUrl(raw) {
  if (!raw) return null;
  return raw.startsWith("http") ? raw : `${API_URL}${raw}`;
}

function RestaurantLogo({ logoUrl, name }) {
  const [failed, setFailed] = useState(false);
  const src = resolveLogoUrl(logoUrl);
  const base = { width: 90, height: 90, borderRadius: "50%", border: "2.5px solid #111", boxShadow: "0 4px 20px rgba(0,0,0,0.14)", flexShrink: 0 };
  if (src && !failed) {
    return (
      <img src={src} alt={name || "Logo"}
        style={{ ...base, objectFit: "cover", display: "block" }}
        onError={() => setFailed(true)} />
    );
  }
  const initials = (name || "").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <div style={{ ...base, background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ fontFamily: "Inter,sans-serif", fontWeight: 800, fontSize: "1.5rem", color: "#fff" }}>{initials}</span>
    </div>
  );
}

export default function SocialConnectPage() {
  const { restaurantSlug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState("form");
  const [successData, setSuccessData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!document.getElementById("sc-css")) {
      const s = document.createElement("style");
      s.id = "sc-css"; s.textContent = CSS;
      document.head.appendChild(s);
    }
    if (!document.getElementById("sc-fonts")) {
      const l = document.createElement("link");
      l.id = "sc-fonts"; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    if (!restaurantSlug) return;
    axios.get(`${API}/public/social-connect/${restaurantSlug}`)
      .then((r) => setConfig(r.data))
      .catch(() => setError("Portal no encontrado o inactivo."))
      .finally(() => setLoading(false));
  }, [restaurantSlug]);

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/social-connect/${restaurantSlug}/register`, formData);
      setSuccessData(r.data);
      setStep("success");
    } catch (err) {
      throw new Error(err.response?.data?.detail || "Error al registrar. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const shell = {
    minHeight: "100vh", background: "#F8F8F6",
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "0 16px 56px", overflowX: "hidden", boxSizing: "border-box", width: "100%",
  };

  if (loading) return (
    <div style={{ ...shell, justifyContent: "center" }}>
      <div style={{ width: 32, height: 32, border: "2.5px solid #E5E7EB", borderTopColor: "#D32F2F", borderRadius: "50%", animation: "spin .9s linear infinite" }} />
    </div>
  );

  if (error || !config) return (
    <div style={{ ...shell, justifyContent: "center", gap: 12 }}>
      <p style={{ fontSize: "2rem" }}>📡</p>
      <p style={{ color: "#6B7280", fontSize: "0.9rem", fontFamily: "Inter,sans-serif" }}>{error || "Portal no disponible."}</p>
    </div>
  );

  const name = config.restaurant_name || "";

  return (
    <div style={shell}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Hero */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "40px 0 22px", gap: "10px" }}>
          <RestaurantLogo logoUrl={config.logo_url} name={name} />
          {name && <p style={{ fontSize: "0.72rem", fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "Inter,sans-serif", margin: 0 }}>{name}</p>}
          <h1 style={{ fontFamily: "Inter,sans-serif", fontWeight: 900, fontSize: "1.55rem", color: "#111827", lineHeight: 1.2, margin: 0, letterSpacing: "-0.02em" }}>
            Wi-Fi Free{name ? ` • ${name}` : ""}
          </h1>
          <p style={{ color: "#6B7280", fontSize: "0.87rem", fontFamily: "Inter,sans-serif", margin: 0, lineHeight: 1.55, maxWidth: 300 }}>
            {config.connect_page_subtitle || "Conéctate en segundos y recibe acceso inmediato al Wi-Fi del restaurante."}
          </p>
        </div>

        {/* Animated transition between form and success */}
        <div key={step} className="sc-fade">
          {step === "form"
            ? <SocialConnectForm config={config} onSubmit={handleSubmit} submitting={submitting} />
            : <SocialConnectSuccess data={successData} config={config} restaurantName={name} />}
        </div>

        {config.show_powered_by_kaiso && (
          <p style={{ textAlign: "center", fontSize: "0.6rem", color: "#D1D5DB", marginTop: 24, fontFamily: "Inter,sans-serif" }}>Powered by Kaiso System</p>
        )}
      </div>
    </div>
  );
}
